---
pageId: concept-tracker
title: Czym jest Tracker
description: Tracker to jedna aktywność albo obszar życia, który zapisujesz w Samlli — z metrykami dopasowanymi do tego, jak o nim myślisz, albo jako dziennik z samych notatek.
category: concepts
order: 1
lang: pl
slug: koncepty/tracker
updated: 2026-08-05
---

<script>
	import { TrackerTypeDemo } from '$lib/marketing/TrackerTypeDemo';
</script>

Tracker to podstawowa jednostka obserwacji w Samlli: jedna aktywność, nawyk albo obszar życia, który zapisujesz. Bieganie, Sen, Nastrój, Oszczędności, Czytanie — każde z nich to tracker z własnym kolorem, ikoną i historią wpisów.

Tracker działa w jednym z dwóch trybów, wybieranym przy tworzeniu:

- **Tryb metryk** — tracker ma jedną lub więcej metryk, czyli wartości, które faktycznie zapisujesz.
- **Tryb dziennika** — zero metryk; każdy wpis to swobodna notatka. Wyłącz przełącznik **Włącz metryki** przy tworzeniu. Trybu nie da się później zmienić.

## Typy metryk

Każda metryka ma typ, który kształtuje jej pole wpisu i statystyki:

- **Liczba** — wartości liczbowe: waga, liczba stron, kilometry. Ma jednostkę.
- **Czas trwania** — wartości czasowe, wpisywane jako „1h 30m" albo „90m".
- **Skala** — ocena ze zdefiniowanym początkiem, końcem i krokiem, jak nastrój 1–10.
- **Tak / Nie** — było albo nie było.
- **Wybór** — jedna wartość z listy, którą sam definiujesz, w Twojej kolejności.

Formularz tworzenia oferuje Liczbę, Czas trwania, Skalę i Wybór; typ Tak / Nie dochodzi przy edycji metryk istniejącego trackera.

## Wypróbuj na żywo

To prawdziwy komponent Samlli, osadzony w dokumentacji:

<TrackerTypeDemo />

## Metryki w praktyce

- **Pierwsza metryka jest główna** — renderuje się dużą czcionką na wpisach i prowadzi statystyki, więc kolejność metryk ustawiaj świadomie.
- **Sam decydujesz, jak wpis zmienia śledzoną wartość**: dodawaj każdy wpis do sumy (przebiegnięty dystans), ustawiaj wartość bieżącą (waga albo stan oszczędności — odczyt jak z licznika), albo agreguj tak, że niższe wartości są lepsze.
- **Konwersje jednostek** pokazują wartość obok w innej jednostce. Surowa wartość nigdy się nie zmienia — jest tylko mnożona przez Twój przelicznik (km → mi używa 0,621).
- Niepotrzebne metryki można dezaktywować zamiast usuwać. Nieaktywne zostają w bazie, ale znikają z formularza wpisu.

## Harmonogram

Nowy tracker startuje z harmonogramem dziennym. Harmonogram decyduje, kiedy tracker pojawia się w „Dziś" — nigdy nie blokuje ręcznego zapisu z innych dni. Możesz powtarzać co N dni, w wybrane dni tygodnia, w dni miesiąca albo w roczną datę — albo usunąć harmonogram całkiem.

## Kolor i kategoria

Tracker pokazuje własny kolor, jeśli go ustawisz; w przeciwnym razie dziedziczy kolor swojej kategorii. Wyczyść kolor trackera, żeby wrócić do koloru kategorii.

## Archiwum i usuwanie

- **Archiwizacja** ukrywa tracker z głównej listy. Wszystkie dane zostają i możesz go przywrócić w każdej chwili z Ustawień.
- **Przeniesienie do kosza** planuje trwałe usunięcie po 30 dniach — do tego czasu tracker wciąż da się przywrócić z Ustawień.

## Limity planów

Plan Free obejmuje 5 trackerów z maksymalnie 3 metrykami każdy. Pro znosi oba limity.

## Przykłady

- **Bieganie** — Dystans (liczba, km), Czas (czas trwania), Średnie tętno (liczba, bpm)
- **Sen** — Długość snu (czas trwania), Jakość snu (skala 1–5)
- **Nastrój** — Nastrój, Energia i Niepokój jako skale 1–10
- **Oszczędności** — Stan oszczędności jako liczba ustawiająca wartość bieżącą
